import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { debounceTime, startWith, tap } from 'rxjs/operators';
import { getBatchActionsSelectedStudentIds } from 'Src/ng2/store';
import { ISelectedBatchActionStudentIds } from '../../../../school/student-level-dashboard/student-level-dash.component';
import { IColumnStuLvl, IGroupData, IListConfig, IRowData, TList } from '../../../models/list-models';
import { BatchEditService } from '../../../services/batch-edit-service/batch-edit-service';
import { CellDisplayService } from '../../../services/list-services/cell-display.service';
import { HeaderService, IDisplayedHeader } from '../../../services/list-services/header.service';
import { SortAndFilterService, TSortDirections } from '../../../services/list-services/sort-and-filter.service';

export interface IBatchActionTableHeaderIsChecked {
  headerName: string;
  isChecked: boolean;
  isIndeterminate: boolean;
  visibleRowData: IRowData[][];
}

export interface IRowDataWithBatchActions {
  [index: number]: IRowData[];
  isChecked: boolean;
}

/* istanbul ignore next */
@Component({
  selector: 'fixed-table',
  template: require('./fixed-table.component.html'),
  styles: [require('./fixed-table.component.scss')],
})
export class FixedTableComponent implements OnInit {
  // REQUIRED bindings if used along with FixedToInfiniteViewComponent
  @Input() schoolId: string;
  @Input() shelterId: string;
  @Input() groupIndx: number;
  @Input() groupData: IGroupData;
  @Input() columns: IColumnStuLvl[];
  @Input() maximumVisibleRowsPerGroup: number;
  @Input() filterFormControl: FormControl;
  @Input() batchActionsMode$: Observable<boolean>;
  @Input() sortKey$: BehaviorSubject<string>;
  @Input() sortDirection$: BehaviorSubject<TSortDirections>;
  @Input() hideHeader: boolean;
  @Input() currentFilterFormValue: string;
  @Input() columnIndexMap: { [key: string]: number };
  @Input() listConfig: IListConfig;
  @Output() sortColUpdate = new EventEmitter<string>();
  @Output() focusedGroup = new EventEmitter<{
    groupData: IGroupData;
    sortKey: string;
    sortDirection: TSortDirections;
    groupIndx: number;
  }>();
  @Output() clickedRowData = new EventEmitter<IRowData[]>();
  @Output() onDynamicComponentClick = new EventEmitter();
  @Output() batchActionData = new EventEmitter();
  @Output() tableHeaderIsChecked = new EventEmitter<IBatchActionTableHeaderIsChecked>();
  @Output() uiRowData = new EventEmitter<IGroupData>();

  // OPTIONAL bindins
  @Input() batchActionsSelectedIds$: Observable<string[]> = null;
  @Output() dynamicComponentClicked = new EventEmitter<any>();
  @Input() dynamicComponentTrigger: null | boolean;

  // methods
  sortRowData: Function = SortAndFilterService.sortTableRowData;
  filterRows: Function = SortAndFilterService.filterRows;
  getDisplayedHeaders: Function = HeaderService.getDisplayedHeaders;
  getHeaderOverflow: Function = HeaderService.getHeaderOverflow;

  // all other props
  displayedHeaders: IDisplayedHeader[];
  tableRows$: BehaviorSubject<IRowData[]>;
  visibleDataSource$ = new BehaviorSubject<IRowData[][] | IRowDataWithBatchActions[][]>([]);
  dataSource$ = new BehaviorSubject<IRowData[][] | IRowDataWithBatchActions[][]>([]);
  hasSearchResult$ = new BehaviorSubject<boolean>(true);
  headerOverflow: boolean = false;
  sectionHeaderIsChecked: boolean;
  sectionHeaderIsInd: boolean;
  dataPipeline$: Observable<any>;
  noDataMessage: string;
  emptyTableMessage: string;
  sortableColumns: boolean = true;
  showAll: boolean = false;
  isBatchActionActivated: boolean;

  constructor(private store: Store<any>, private batchEditService: BatchEditService) {}

  ngOnInit(): void {
    const { showAll, rowData } = this.groupData;
    this.emptyTableMessage = this.listConfig.emptyTableMessage;
    this.noDataMessage = this.listConfig.noDataMessage;
    this.sortableColumns = this.listConfig.sortableColumns;
    this.showAll = !!showAll;
    this.tableRows$ = new BehaviorSubject<any[]>(rowData);
    this.displayedHeaders = this.getDisplayedHeaders(this.columns, this.groupData);
    this.noDataMessage = this.listConfig.noDataMessage;
    this.dataPipeline$ = combineLatest(
      this.tableRows$,
      this.filterFormControl.valueChanges.pipe(
        debounceTime(150),
        startWith(this.currentFilterFormValue),
      ),
      this.batchActionsMode$,
      this.sortKey$,
      this.sortDirection$,
      this.batchActionsSelectedIds$ || this.store.select(getBatchActionsSelectedStudentIds),
    ).pipe(
      tap(([rowData, filterTerm, isBatchActionActive, sortKey, sortDirection, batchEditIds]) => {
        // for keeping track of batchActionsMode$ without manual subscription across the component lifecycle
        this.isBatchActionActivated = isBatchActionActive;
        if (!isBatchActionActive) this.sectionHeaderIsChecked = false;
        const sortKeyIndex = this.columnIndexMap[sortKey];
        const { listType } = this.listConfig;
        const filteredRowData = this.filterRows({ filterTerm, listType, rowData });
        const sortedRowData = this.sortRowData(filteredRowData, sortKeyIndex, sortDirection, sortKey);

        const formattedRowData = CellDisplayService.getFormattedRowData({
          listType,
          sortedRowData,
          columns: this.columns,
        });

        this.uiRowData.emit({ ...this.groupData, ...{ rowData: formattedRowData } });

        const rowDataWithOptionalBatchActions = isBatchActionActive
          ? this.getExistingBatchActionSelections(formattedRowData, batchEditIds)
          : formattedRowData;

        let visibleRows;
        if (this.showAll) {
          visibleRows = rowDataWithOptionalBatchActions;
        } else {
          visibleRows = rowDataWithOptionalBatchActions.slice(0, this.maximumVisibleRowsPerGroup);
        }

        const noSearchMatches = filterTerm !== '' && visibleRows.length === 0;
        const showSearchResults = noSearchMatches ? false : true;

        this.hasSearchResult$.next(showSearchResults);
        this.showAll = showAll;
        this.dataSource$.next(rowDataWithOptionalBatchActions);
        this.visibleDataSource$.next(visibleRows);
      }),
    );
  }

  emitUpdatedSortCol(col: string): void {
    if (this.sortableColumns) {
      this.sortColUpdate.emit(col);
    }
  }

  emitFocusedGroup(): void {
    this.focusedGroup.emit({
      groupData: this.groupData,
      sortKey: this.sortKey$.value,
      sortDirection: this.sortDirection$.value,
      groupIndx: this.groupIndx,
    });
  }

  emitRowClick(rowData: IRowData[], dataColumn?): void {
    if (dataColumn && dataColumn.dynamic) return;

    if (!this.isBatchActionActivated) {
      this.clickedRowData.emit(rowData);
    } else {
      this.emitBatchActionRowId(rowData);
    }
  }

  emitUiRowData(uiRowData: IGroupData): void {
    this.uiRowData.emit(uiRowData);
  }

  emitDynamicComponent(data: any): void {
    this.dynamicComponentClicked.emit(data);
  }

  getExistingBatchActionSelections(rowData: IRowData[][], batchEditIds: string[]): IRowDataWithBatchActions[][] {
    const rowIdHash = this.batchEditService.getSelectedRowsHash(batchEditIds);
    const listType: TList = this.listConfig.listType;
    const { mappedData, count } = this.batchEditService.getRowDataWithBatchActions(rowIdHash, rowData, listType);
    this.sectionHeaderIsChecked = rowData.length > 0 && rowData.length === count ? true : false;
    this.sectionHeaderIsInd = count && rowData.length > count ? true : false;
    const headerName = this.displayedHeaders[0].human;
    this.tableHeaderIsChecked.emit({
      headerName,
      isChecked: this.sectionHeaderIsChecked,
      isIndeterminate: this.sectionHeaderIsInd,
      visibleRowData: rowData,
    });
    return mappedData;
  }

  emitBatchActionRowId(data: IRowData[]): void {
    this.sectionHeaderIsChecked = false;
    const batchData = { data, updateAll: this.sectionHeaderIsChecked, level: 'ROW' };
    this.batchActionData.emit(batchData);
  }

  emitBatchActionSectionIds(): void {
    this.sectionHeaderIsChecked = !this.sectionHeaderIsChecked;
    const data = this.dataSource$.value;
    const batchData = { data, updateAll: this.sectionHeaderIsChecked, level: 'SECTION' };
    this.batchActionData.emit(batchData);
  }

  checkHeaderOverflow(className: string): void {
    this.headerOverflow = this.getHeaderOverflow(className);
  }

  resetHeaderOverflow(): void {
    this.headerOverflow = false;
  }
}
